import { create } from 'zustand'

export interface QuestionTally {
  questionId: string
  kind: 'OPEN' | 'CLOSED'
  total: number
  closed?: { optionId: string; count: number; voters?: { identity: string; name: string }[] }[]
  open?: { answerId: string; text: string; name?: string }[]
}
export interface PollRunFull {
  run: { id: string; channelId: string; state: string; activeQuestionId: string | null; pollSetId: string }
  isAnonymous: boolean
  questions: {
    id: string
    order: number
    kind: 'OPEN' | 'CLOSED'
    text: string
    options: { id: string; text: string; order: number }[]
  }[]
  tallies: QuestionTally[]
}
export interface PresentationRunFull {
  run: { id: string; channelId: string; state: string; currentSlideIndex: number; presentationId: string }
  presentation: { id: string; title: string; slideCount: number } | null
  slides: { id: string; index: number; imageKey: string }[]
}

interface LiveEventState {
  poll: PollRunFull | null
  myVotes: Record<string, boolean> // questionId -> voted
  presentation: PresentationRunFull | null
  // OSPROD-272: run id the LOCAL viewer closed the presentation overlay
  // for. The run stays ACTIVE for everyone else (host-only-closes
  // semantics) — this only hides the overlay on THIS client, replaced by
  // a "Reopen" chip. Cleared when a different run starts or on reset.
  presentationDismissedRunId: string | null
  setPoll: (p: PollRunFull | null) => void
  applyTally: (runId: string, questionId: string, tally: QuestionTally) => void
  markVoted: (questionId: string) => void
  setMyVotes: (map: Record<string, boolean>) => void
  setPresentation: (p: PresentationRunFull | null) => void
  setSlideIndex: (runId: string, index: number) => void
  dismissPresentation: (runId: string | null) => void
  reset: () => void
}

// OSPROD-272: the dismissal has to outlive the store. `reset()` runs on every
// channel switch, so an in-memory-only flag meant the presentation re-opened
// itself each time the viewer walked back into the room — read by QA as "it
// keeps coming back even after I deleted the document". Session-scoped so a
// new browser session still shows an ongoing presentation once.
const DISMISS_KEY = 'rx2go-presentation-dismissed-run'
const readDismissed = (): string | null => {
  try {
    return sessionStorage.getItem(DISMISS_KEY)
  } catch {
    return null // private-mode / storage-disabled — degrade to in-memory
  }
}
const writeDismissed = (runId: string | null) => {
  try {
    if (runId) sessionStorage.setItem(DISMISS_KEY, runId)
    else sessionStorage.removeItem(DISMISS_KEY)
  } catch {
    /* best-effort */
  }
}

export const useLiveEventStore = create<LiveEventState>((set) => ({
  poll: null,
  myVotes: {},
  presentation: null,
  presentationDismissedRunId: readDismissed(),
  // Preserve myVotes only while the SAME run stays active; a new run (or clear)
  // resets which questions this user has answered.
  setPoll: (p) =>
    set((s) => ({
      poll: p,
      myVotes: p && s.poll && s.poll.run.id === p.run.id ? s.myVotes : {},
    })),
  setMyVotes: (map) => set({ myVotes: map }),
  applyTally: (runId, questionId, tally) =>
    set((s) => {
      if (!s.poll || s.poll.run.id !== runId) return s
      return {
        poll: {
          ...s.poll,
          tallies: s.poll.tallies.map((t) => (t.questionId === questionId ? tally : t)),
        },
      }
    }),
  markVoted: (questionId) => set((s) => ({ myVotes: { ...s.myVotes, [questionId]: true } })),
  // Keep the local dismissal only while the SAME run stays active — a new
  // run must pop the overlay back up for everyone, including viewers who
  // locally closed the previous one.
  setPresentation: (p) =>
    set(() => {
      // Re-read the persisted dismissal: `reset()` wipes the in-memory copy on
      // every channel switch, so the store alone can't tell "already dismissed
      // this run" from "never seen it".
      const dismissed = p && readDismissed() === p.run.id ? p.run.id : null
      if (!dismissed) writeDismissed(null)
      return { presentation: p, presentationDismissedRunId: dismissed }
    }),
  setSlideIndex: (runId, index) =>
    set((s) =>
      s.presentation && s.presentation.run.id === runId
        ? { presentation: { ...s.presentation, run: { ...s.presentation.run, currentSlideIndex: index } } }
        : s,
    ),
  dismissPresentation: (runId) => {
    writeDismissed(runId)
    set({ presentationDismissedRunId: runId })
  },
  // Storage is deliberately NOT cleared here — `reset()` fires on channel
  // switches, and forgetting the dismissal there is exactly the bug.
  reset: () => set({ poll: null, myVotes: {}, presentation: null, presentationDismissedRunId: null }),
}))
